import { Component, EventEmitter, OnDestroy, OnInit, AfterViewInit, Output, ChangeDetectorRef, ElementRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { TrackUntrackSchLService } from './track-untrack-sch-l.service';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-track-untrack-sch-l',
    templateUrl: './track-untrack-sch-l.component.html',
    styleUrls: ['./track-untrack-sch-l.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class TrackUntrackSchLComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // 🚀 CRITICAL: Filter params input for dynamic data
  filterParams: any = {};

  // Modal properties
  showModal: boolean = false;
  modalTitle: string = '';
  minimise: boolean = false;

  // Notification/Submission fields
  FTR_NOTIFICATION_PROVIDED: string = '';
  HOW_NOTIFICATION_PROVIDED: string = '';
  ADJUSTMENT_SUBMITTED: string = '';
  HOW_ADJUSTMENT_SUBMITTED: string = '';
  yesNoOptions: any[] = [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' }
  ];

  baseURLs: any = {
    admin: '',
    api: '',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: '',
  };

  rowData: any = {};

  gridData: any = {};

  // Component properties following update-change-list pattern
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  syncSaveDtls: any[] = [];
  save_json: any = {};
  logged_in_user: string = '';
  modalName: string = '';
  aggLimit: string = '';
  returnClobSettingsObj: any[] = [];

  // Loading states
  isSaveClicked: boolean = false;
  crudLoading: boolean = false;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private gtwSnackbarService: GtwSnackbarService,
    private trackUntrackSchLService: TrackUntrackSchLService,
    private cdr: ChangeDetectorRef,
    private elRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngAfterViewInit(): void {
    const host = this.elRef?.nativeElement;
    if (!host) return;
    const modalDialog = host.closest('.modal-dialog');
    if (modalDialog) {
      modalDialog.style.width = '400px';
      modalDialog.style.maxWidth = '400px';
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.globalService = header.globalParams || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const row = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(row) ? (row[0] || {}) : row;
    this.gridData = gridPayloadFromActiveScreen(activeScreen) || {};
    this.modalName =
      activeScreen?.data?.modalName || activeScreen?.data?.modal_name || '';
  }

  init(): void {
    // Following update-change-list pattern - check if all required data is available
    if (
      this.baseURLs &&
      Object.keys(this.baseURLs).length > 0 &&
      this.filterParams &&
      Object.keys(this.filterParams).length > 0 &&
      this.GENERAL_CONFIG &&
      Object.keys(this.GENERAL_CONFIG).length > 0 &&
      this.gridData &&
      Object.keys(this.gridData).length > 0 &&
      this.modalName &&
      this.modalName != null &&
      this.modalName != undefined &&
      this.modalName != ''
    ) {
      this.showModal = true; // Show modal when all data is ready
      this.initializeData();
    }
  }

  initializeData(): void {
    // Get logged in user from USER_SETTINGS
    if (this.USER_SETTINGS?.user?.sso_id) {
      this.logged_in_user = this.USER_SETTINGS.user.sso_id;
    }

    // Get modal name from globalService or filterParams (if not already set via @Input)
    if (!this.modalName && this.globalService?.modalScreen?.modal_name) {
      this.modalName = this.globalService.modalScreen.modal_name;
    }

    // Set modal title for notification/submission status update
    this.modalTitle = 'Update Notification/Submission Status';

    // Process grid data if rowData is available
    if (this.gridData) {
      this.processGridData();
    }

    // Initialize notification/submission fields
    this.setNotificationDefaults();
  }

  private setNotificationDefaults(): void {
    let firstDataItem: any = null;
    if (this.gridData) {
      const tempGridData = _.clone(this.gridData);
      for (const key in tempGridData) {
        firstDataItem = tempGridData[key].data || tempGridData[key];
        break;
      }
    }

    this.FTR_NOTIFICATION_PROVIDED = this.getFallbackValue(
      firstDataItem?.FTR_NOTIFICATION_PROVIDED,
      this.rowData?.FTR_NOTIFICATION_PROVIDED
    ) || '';
    this.HOW_NOTIFICATION_PROVIDED = this.getFallbackValue(
      firstDataItem?.HOW_NOTIFICATION_PROVIDED,
      this.rowData?.HOW_NOTIFICATION_PROVIDED
    ) || '';
    this.ADJUSTMENT_SUBMITTED = this.getFallbackValue(
      firstDataItem?.ADJUSTMENT_SUBMITTED,
      this.rowData?.ADJUSTMENT_SUBMITTED
    ) || '';
    this.HOW_ADJUSTMENT_SUBMITTED = this.getFallbackValue(
      firstDataItem?.HOW_ADJUSTMENT_SUBMITTED,
      this.rowData?.HOW_ADJUSTMENT_SUBMITTED
    ) || '';
  }

  processGridData(): void {
    // Process grid data exactly like aggregration-rerun component
    if (this.gridData != null) {
      this.syncSaveDtls = [];
      this.save_json = {};

      // 🚀 CRITICAL: Calculate col_value based on modal name
      // If modal name is "Track Schedule L", set col_value as 1, otherwise 0
      const colValue = this.modalName === 'trackScheduleL' ? 1 : 0;

      // Initialize returnClobSettingsObj as array
      this.returnClobSettingsObj = [];

      // Clone the grid data like update-change-list
      const tempGridData = _.clone(this.gridData);
      const gridDataMultipleSelect = _.clone(tempGridData);

      if (gridDataMultipleSelect != undefined) {
        // Extract tax_year, scenario, jcd_key with proper fallbacks (use first item or fallbacks)
        let firstDataItem: any = null;
        for (var key in gridDataMultipleSelect) {
          firstDataItem = gridDataMultipleSelect[key].data || gridDataMultipleSelect[key];
          break; // Get first item for save_json
        }

        // Use fallback to first item or other sources for tax year
        const taxYear = this.getFallbackValue(
                         firstDataItem?.TAX_YEAR,
                         this.filterParams?.tax_year,
                         this.globalService?.tax_year,
                         this.globalService?.globalParams?.tax_year,
                         this.rowData?.TAX_YEAR
                       );
        const scenario = this.getFallbackValue(
          firstDataItem?.SCENARIO,
          this.filterParams?.scenario,
          this.globalService?.scenario,
          this.globalService?.globalParams?.scenario,
          this.rowData?.SCENARIO
        );
        const jcdKey = this.getFallbackValue(
          firstDataItem?.JCD_KEY,
          this.filterParams?.jcd_key,
          this.globalService?.jcd_key,
          this.globalService?.globalParams?.jcd_key,
          this.rowData?.JCD_KEY
        );

        // Create single save_json object with linear data
        this.save_json = {
          COL_VALUE: colValue,
          tax_year: taxYear,
          scenario: scenario,
          jcd_key: jcdKey,
        };

        // Process all items for returnClobSettingsObj and syncSaveDtls
        for (var key in gridDataMultipleSelect) {
          const gridObj: any = {};

          // Access data property like AngularJS: vm.gridDataMultipleSelect[key].data
          const dataItem =
            gridDataMultipleSelect[key].data || gridDataMultipleSelect[key];

          // Extract relevant fields based on track-untrack-sch-l requirements
          // Safe check: Skip if dataItem is null/undefined
          if (!dataItem) {
            continue;
          }

          // Row 1 fields
          gridObj['UNIQUE_ID'] = dataItem?.UNIQUE_ID || null;
          gridObj['COMBINATION_KEY'] = dataItem?.COMBINATION_KEY || null;
          gridObj['STATUS'] = dataItem?.STATUS || null;
          gridObj['TAX_YEAR'] = dataItem?.TAX_YEAR || null;
          gridObj['FILING_TYPE'] = dataItem?.FILING_TYPE || null;
          gridObj['PARENT_LEID'] = dataItem?.PARENT_LEID || null;
          gridObj['LEID'] = dataItem?.LEID || null;
          gridObj['TAX_CODE'] = dataItem?.TAX_CODE || null;
          gridObj['ENTITY_NAME'] = dataItem?.ENTITY_NAME || null;
          gridObj['ME_NAME'] = dataItem?.ME_NAME || null;
          gridObj['LEID_COUNTRY'] = dataItem?.LEID_COUNTRY || null;
          gridObj['CURRENCY'] = dataItem?.CURRENCY || null;

          // Row 2 fields
          gridObj['TAXABLE_INCOME_LOSS'] = dataItem?.TAXABLE_INCOME_LOSS || null;
          gridObj['TAX_LIAB_NON_CREDIT_US'] = dataItem?.TAX_LIAB_NON_CREDIT_US || null;
          gridObj['TAX_LIAB_CREDIT_US'] = dataItem?.TAX_LIAB_CREDIT_US || null;
          gridObj['TAX_RETURN_ATTACHMENT'] = dataItem?.TAX_RETURN_ATTACHMENT || null;
          gridObj['ISSUBJECTTO901'] = dataItem?.ISSUBJECTTO901 || null;
          gridObj['DESCRIPTION'] = dataItem?.DESCRIPTION || null;
          gridObj['FTR_DESCRIPTION'] = dataItem?.FTR_DESCRIPTION || null;
          gridObj['FTR_RELATES_DESC'] = dataItem?.FTR_RELATES_DESC || null;
          gridObj['TRANS_DETAILS_KEY'] = dataItem?.TRANS_DETAILS_KEY || null;

          // Row 3 fields (only up to Comments)
          gridObj['DATE_PROVIDED_DESC'] = dataItem?.DATE_PROVIDED_DESC || null;
          gridObj['FC_AMT'] = dataItem?.FC_AMT || null;
          gridObj['ADJUSTMENT'] = dataItem?.ADJUSTMENT || null;
          gridObj['TRANS_DATE'] = dataItem?.TRANS_DATE || null;
          gridObj['DETAIL_COMMENTS'] = dataItem?.DETAIL_COMMENTS || null;

          // Add gridObj to returnClobSettingsObj
          this.returnClobSettingsObj.push(gridObj);

          // Push to syncSaveDtls
          this.syncSaveDtls.push({
            tax_year: dataItem.TAX_YEAR,
          });
        }
      }
    }
  }

  /**
   * Get fallback value from multiple sources
   * Returns the first non-null/non-undefined value, or null if all are null/undefined
   */
  private getFallbackValue(...values: any[]): any {
    return values.find((val) => val != null && val !== undefined) ?? null;
  }

  cancel(): void {
    // Close modal and emit cancel event
    this.showModal = false;

    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true,
    });

    if (document.querySelector('gtw-track-untrack-sch-l-ce')) {
      document
        .querySelector('gtw-track-untrack-sch-l-ce')
        ?.dispatchEvent(updateEvent);
    }
  }

  save(): void {
    // Prevent double-click
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar(
        'Please wait while we process this request',
        'gtw-snackbar--yellow'
      );
      return;
    }

    // Validation: Check if at least one field is selected
    if (!this.FTR_NOTIFICATION_PROVIDED && !this.ADJUSTMENT_SUBMITTED) {
      this.gtwSnackbarService.openSnackBar(
        'Please select at least one option',
        'gtw-snackbar--red'
      );
      return;
    }

    // Validation: Check if text is provided when "Yes" is selected
    if (this.FTR_NOTIFICATION_PROVIDED === 'Yes' && !this.HOW_NOTIFICATION_PROVIDED?.trim()) {
      this.gtwSnackbarService.openSnackBar(
        'Please provide how FTR notification was provided',
        'gtw-snackbar--red'
      );
      return;
    }

    if (this.ADJUSTMENT_SUBMITTED === 'Yes' && !this.HOW_ADJUSTMENT_SUBMITTED?.trim()) {
      this.gtwSnackbarService.openSnackBar(
        'Please provide how adjustment was submitted',
        'gtw-snackbar--red'
      );
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    // Call confirmTrackUntrack
    this.confirmTrackUntrack();
  }

  confirmTrackUntrack(): void {
    // Follow exact update-change-list behavior with entity limit validation
    if (this.syncSaveDtls.length > parseInt(this.aggLimit)) {
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.gtwSnackbarService.openSnackBar(
        "Can't select more than " + this.aggLimit + ' entities',
        'gtw-snackbar--red'
      );
      return;
    }

    // Add notification fields to save_json
    this.save_json.FTR_NOTIFICATION_PROVIDED = this.FTR_NOTIFICATION_PROVIDED;
    this.save_json.HOW_NOTIFICATION_PROVIDED = this.FTR_NOTIFICATION_PROVIDED === 'Yes' ? this.HOW_NOTIFICATION_PROVIDED : '';
    this.save_json.ADJUSTMENT_SUBMITTED = this.ADJUSTMENT_SUBMITTED;
    this.save_json.HOW_ADJUSTMENT_SUBMITTED = this.ADJUSTMENT_SUBMITTED === 'Yes' ? this.HOW_ADJUSTMENT_SUBMITTED : '';

    const message = 'Changes saved successfully.';

    // Call sendDetails function
    this.sendDetails(this.returnClobSettingsObj, this.save_json, message);
  }

  sendDetails(
    returnClobSettingsObj: any,
    save_json: any,
    message: string
  ): void {
    // Call service method
    this.trackUntrackSchLService
      .trackUntrackSchL(
        this.GENERAL_CONFIG.base_url,
        returnClobSettingsObj,
        save_json,
        this.globalService,
        this.filterParams,
        this.logged_in_user,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (result: any) => {
          this.isSaveClicked = false;
          this.crudLoading = false;

          // Handle response
          if (result.errorMessage && result.errorMessage !== 'null') {
            // Show error message but keep modal open
            this.gtwSnackbarService.openSnackBar(
              result.errorMessage,
              'gtw-snackbar--red'
            );
          } else {
            // Show success message
            this.gtwSnackbarService.openSnackBar(
              message,
              'gtw-snackbar--green'
            );

            // CRITICAL: Emit 'refresh' string for AngularJS modal close
            this.requestGridUpdate.emit('refresh');

            // Close the modal after successful update
            this.cancel();
          }
          this.cdr.detectChanges();
        },
        (error: any) => {
          this.isSaveClicked = false;
          this.crudLoading = false;

          console.error('Track/Untrack Sch L error:', error);
          this.gtwSnackbarService.openSnackBar(
            'Error occurred while tracking/untracking Sch L. Please try again.',
            'gtw-snackbar--red'
          );
          this.cdr.detectChanges();
        }
      );
  }
}
